import type { WaiverGround } from '@rheora/enrollment-contract';

/**
 * Which PUCT §25.478 deposit-waiver grounds to OFFER this customer.
 *
 * ── ONLY ONE OF THE FIVE CAN BE FILTERED, AND THAT IS NOT A SHORTCUT ───────
 * The request was "hide the options that do not apply to me". Exactly one ground is
 * something this app can evaluate:
 *
 *   AGE_62_PLUS            certification: SELF_ATTESTED_AGE      ← WE HAVE THE DOB
 *   FAMILY_VIOLENCE        certification: FAMILY_VIOLENCE_CERTIFICATION (agency/court)
 *   MEDICAL_INDIGENCE      certification: MEDICAL_STATEMENT (physician/hospital)
 *   LOW_INCOME_INSTALLMENT certification: LOW_INCOME_PROOF
 *   GUARANTOR              certification: GUARANTOR_AGREEMENT
 *
 * The other four rest on documents held by a third party — a court, a doctor, an
 * income assessor. Nothing in the enrollment tells us whether they apply, and there
 * is no signal we could infer one from.
 *
 * ⚠️ SO THEY MUST STAY VISIBLE. Hiding a ground we merely have no evidence for would
 * remove a statutory entitlement from the only screen that offers it — a customer
 * fleeing family violence, or one with a physician's statement in hand, would simply
 * never be shown the route the rule grants them. "We do not know" is not "does not
 * apply". Guessing here fails in the direction that harms the customer, so the guess
 * is not made.
 *
 * ⚠️ AND THE AGE FILTER FAILS OPEN. If the date of birth is unknown or unparseable —
 * which is the NORMAL state after a browser refresh, since the DOB deliberately does
 * not rehydrate — the age ground is still offered. A parsing failure must never be
 * what costs someone a waiver; the BFF verifies the attestation regardless.
 */

/** PUCT §25.478(e) — the age at which the age-based waiver becomes available. */
export const WAIVER_AGE_THRESHOLD_YEARS = 62;

export function applicableWaiverGrounds(
  offered: readonly WaiverGround[],
  customerAgeYears: number | undefined,
): readonly WaiverGround[] {
  // Fail open on an unknown or nonsense age — see the header.
  if (customerAgeYears === undefined || !Number.isFinite(customerAgeYears)) return offered;
  if (customerAgeYears >= WAIVER_AGE_THRESHOLD_YEARS) return offered;
  return offered.filter((ground) => ground !== 'AGE_62_PLUS');
}
